import React from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination, Navigation, EffectFade } from 'swiper/modules';
import { NavLink } from 'react-router-dom';
import { IoIosArrowForward, IoIosArrowBack } from 'react-icons/io';

import 'swiper/css';
import 'swiper/css/effect-fade';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import { PageSkeleton } from "./PageSkeleton";
import { truncateText } from '../utils/utils.fucntions';
import { WhyChooseUs } from './subcomponents/WhychoosUs';
import { useIsMobile } from '../contexts/MobileContext';
import { routes } from '../utils/utils.routes';

const ArrowForward = IoIosArrowForward as any;
const ArrowBack = IoIosArrowBack as any;

export const HeroCarousel: React.FC<HeroCarouselProps> = ({ articles, isLoading }) => {
    const { isMobile } = useIsMobile();

    if (isLoading) {
        return <PageSkeleton />;
    }

    if (!articles || articles.length === 0) {
        return null;
    }

    return (
        <div className="relative w-full hero-carousel-container">
            {/* Swiper wrapper: relative so nav buttons center on the slide only */}
            <div className="relative">
                <Swiper
                    modules={[Autoplay, Pagination, Navigation, EffectFade]}
                    effect="fade"
                    autoplay={{ delay: 5500, disableOnInteraction: true }}
                    pagination={{ clickable: true }}
                    navigation={{
                        nextEl: '.swiper-button-next-custom',
                        prevEl: '.swiper-button-prev-custom',
                    }}
                    loop={true}
                    className="h-[380px] sm:h-[420px] md:h-[480px] lg:h-[560px]"
                >
                    {articles.map((article) => (
                        <SwiperSlide key={article.id} className="!h-full">
                            <div
                                className="relative w-full h-full bg-center bg-no-repeat"
                                style={{ backgroundImage: `url(${article.coverImage})`, backgroundSize: 'cover' }}
                            >
                                {/* Rich multi-layer gradient overlay */}
                                <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/35 to-transparent" />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />

                                <div className="relative z-10 h-full flex items-center">
                                    <div className="container mx-auto px-6 xl:px-8 lg:px-8 md:px-8">
                                        <div className="max-w-xl lg:max-w-2xl text-white hero-slide-content">
                                            {/* Animated category badge */}
                                            <span className="hero-badge inline-flex items-center gap-1.5 px-3 py-1 text-[10px] font-bold uppercase bg-primary rounded-full mb-4 tracking-widest shadow-lg">
                                                <span className="hero-badge-dot w-1.5 h-1.5 rounded-full bg-white/80 animate-pulse" />
                                                {article.category}
                                            </span>

                                            <h2 className="text-xl sm:text-2xl md:text-2xl lg:text-3xl xl:text-3xl font-bold leading-tight mb-3 drop-shadow-md">
                                                {isMobile ? truncateText(article.title, 55) : article.title}
                                            </h2>

                                            <p className="text-sm md:text-sm text-white/80 mb-5 line-clamp-2 max-w-lg leading-relaxed drop-shadow-sm">
                                                {truncateText(article.excerpt, 160)}
                                            </p>

                                            <NavLink
                                                to={`${routes.ARTICLESDETAILS}/${article.slug}`}
                                                className="hero-cta-btn inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white rounded-full transition-all duration-300"
                                                style={{ backdropFilter: 'blur(12px)' }}
                                            >
                                                Lire la suite
                                                <ArrowForward size={16} className="hero-cta-arrow" />
                                            </NavLink>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </SwiperSlide>
                    ))}
                </Swiper>

                {/* Nav buttons anchored to the Swiper wrapper */}
                <button
                    id="hero-prev-btn"
                    className="swiper-button-prev-custom hero-nav-btn absolute left-4 top-1/2 -translate-y-1/2 z-20 text-white rounded-full transition-all duration-300 focus:outline-none"
                    aria-label="Diapositive précédente"
                >
                    <ArrowBack size={20} />
                </button>
                <button
                    id="hero-next-btn"
                    className="swiper-button-next-custom hero-nav-btn absolute right-4 top-1/2 -translate-y-1/2 z-20 text-white rounded-full transition-all duration-300 focus:outline-none"
                    aria-label="Diapositive suivante"
                >
                    <ArrowForward size={20} />
                </button>
            </div>

            <WhyChooseUs />
        </div>
    );
};